import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { DollarSign, Users, TrendingUp, Clock, Calendar, Filter, RefreshCw, User, Plus, StickyNote } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to get auth headers from localStorage or sessionStorage
const getAuthHeaders = () => {
  const sessionToken = localStorage.getItem('session_token') || sessionStorage.getItem('session_token');
  return sessionToken ? { 'Authorization': `Bearer ${sessionToken}` } : {};
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('all');
  const [selectedReferral, setSelectedReferral] = useState('all');
  const [referralsList, setReferralsList] = useState([]);
  const [sharedNotes, setSharedNotes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${API}/partner/notes`, true);
    Object.entries(getAuthHeaders()).forEach(([k, v]) => xhr.setRequestHeader(k, v));
    xhr.onload = () => {
      if (xhr.status === 200) {
        try { setSharedNotes(JSON.parse(xhr.responseText).notes || []); } catch { /* ignore */ }
      }
    };
    xhr.send();
  }, []);

  useEffect(() => {
    fetchStats();
  }, [dateRange, selectedReferral]);

  const getDateRange = () => {
    const now = new Date();
    let start_date = null;
    let end_date = now.toISOString();

    switch (dateRange) {
      case 'today':
        start_date = new Date(now.setHours(0, 0, 0, 0)).toISOString();
        break;
      case 'week':
        const weekAgo = new Date();
        weekAgo.setDate(weekAgo.getDate() - 7);
        start_date = weekAgo.toISOString();
        break;
      case 'month':
        const monthAgo = new Date();
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        start_date = monthAgo.toISOString();
        break;
      case 'quarter':
        const quarterAgo = new Date();
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        start_date = quarterAgo.toISOString();
        break;
      case 'year':
        const yearAgo = new Date();
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        start_date = yearAgo.toISOString();
        break;
      default:
        return { start_date: null, end_date: null };
    }

    return { start_date, end_date };
  };

  const fetchStats = async () => {
    try {
      setLoading(true);
      const { start_date, end_date } = getDateRange();
      
      let url = `${API}/dashboard/stats`;
      const params = new URLSearchParams();
      
      if (start_date) params.append('start_date', start_date);
      if (end_date) params.append('end_date', end_date);
      if (selectedReferral !== 'all') params.append('referral_id', selectedReferral);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        cache: 'no-store',
      });
      
      if (!response.ok) throw new Error('Failed to fetch stats');
      
      const data = await response.json();
      setStats(data);
      
      // Update referrals list for dropdown
      if (data.all_referrals_list) {
        setReferralsList(data.all_referrals_list);
      }
    } catch (error) {
      toast.error('Failed to load dashboard stats');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setDateRange('all');
    setSelectedReferral('all');
  };

  const hasActiveFilters = dateRange !== 'all' || selectedReferral !== 'all';

  const statCards = [
    {
      title: 'Total Commission',
      value: `₹${stats?.total_commission?.toFixed(2) || '0.00'}`,
      icon: DollarSign,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      testId: 'total-commission-card'
    },
    {
      title: 'Pending Commission',
      value: `₹${stats?.pending_commission?.toFixed(2) || '0.00'}`,
      icon: Clock,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      testId: 'pending-commission-card'
    },
    {
      title: 'Total Referrals',
      value: stats?.total_referrals || 0,
      icon: Users,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      testId: 'total-referrals-card'
    },
    {
      title: 'Paid Referrals',
      value: stats?.paid_referrals || 0,
      icon: TrendingUp,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      testId: 'paid-referrals-card'
    },
  ];

  return (
    <div className="space-y-8">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Dashboard
          </h1>
          <p className="text-muted-foreground text-sm lg:text-base">Track your performance and earnings</p>
        </div>
        <div className="flex gap-2">
          <Button 
            onClick={() => navigate('/referrals')} 
            className="bg-orange-500 hover:bg-orange-600"
            data-testid="add-referral-dashboard-button"
          >
            <Plus className="h-4 w-4 mr-2" />
            Add Referral
          </Button>
          <Button onClick={fetchStats} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Shared notes from Kilobeaters team */}
      {sharedNotes.length > 0 && (
        <Card className="border-orange-200 bg-orange-50/50" data-testid="shared-notes-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <StickyNote className="h-4 w-4 text-orange-600" />
              Notes from your Kilobeaters contact
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {sharedNotes.map(n => (
              <div key={n.note_id} className="bg-white border rounded-md p-3" data-testid={`shared-note-${n.note_id}`}>
                <div className="text-xs text-muted-foreground mb-1 flex items-center gap-2">
                  <span className="capitalize">{n.kind}</span>
                  <span>·</span>
                  <span>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-sm whitespace-pre-wrap">{n.body}</div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="h-4 w-4" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Time Period
              </label>
              <Select value={dateRange} onValueChange={setDateRange}>
                <SelectTrigger className="w-[180px]" data-testid="date-filter">
                  <SelectValue placeholder="Select period" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Time</SelectItem>
                  <SelectItem value="today">Today</SelectItem>
                  <SelectItem value="week">Last 7 Days</SelectItem>
                  <SelectItem value="month">Last 30 Days</SelectItem>
                  <SelectItem value="quarter">Last 3 Months</SelectItem>
                  <SelectItem value="year">Last Year</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium text-muted-foreground flex items-center gap-2">
                <User className="h-4 w-4" />
                Referral
              </label>
              <Select value={selectedReferral} onValueChange={setSelectedReferral}>
                <SelectTrigger className="w-[200px]" data-testid="referral-filter">
                  <SelectValue placeholder="Select referral" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Referrals</SelectItem>
                  {referralsList.map((ref) => (
                    <SelectItem key={ref.referral_id} value={ref.referral_id}>
                      {ref.customer_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                Clear Filters
              </Button>
            )}
          </div>
          
          {hasActiveFilters && (
            <div className="mt-4 flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
              <span>Showing data for:</span>
              {dateRange !== 'all' && (
                <span className="px-2 py-1 bg-primary/10 text-primary rounded-md text-xs font-medium">
                  {dateRange === 'today' ? 'Today' : 
                   dateRange === 'week' ? 'Last 7 Days' :
                   dateRange === 'month' ? 'Last 30 Days' :
                   dateRange === 'quarter' ? 'Last 3 Months' : 'Last Year'}
                </span>
              )}
              {selectedReferral !== 'all' && (
                <span className="px-2 py-1 bg-blue-500/10 text-blue-600 rounded-md text-xs font-medium">
                  {referralsList.find(r => r.referral_id === selectedReferral)?.customer_name || 'Selected Referral'}
                </span>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats Grid */}
      {loading ? (
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : (
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
              >
                <Card data-testid={stat.testId} className="hover:shadow-md transition-all duration-200">
                  <CardHeader className="flex flex-row items-center justify-between pb-2">
                    <CardTitle className="text-sm font-medium text-muted-foreground">
                      {stat.title}
                    </CardTitle>
                    <div className={`p-2 rounded-lg ${stat.bgColor}`}>
                      <Icon className={`h-5 w-5 ${stat.color}`} />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      {stat.value}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Tier Information Card */}
      {stats?.tier_info && (
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Your Partnership Tier</span>
              <span className="text-3xl font-bold text-primary" style={{ fontFamily: 'Outfit, sans-serif' }}>
                {stats.tier_info.tier_name}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-6 ${stats.tier_info.retainer > 0 ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Commission Rate</p>
                <p className="text-2xl font-bold text-primary">{(stats.tier_info.commission_rate * 100).toFixed(0)}%</p>
              </div>
              {stats.tier_info.retainer > 0 && (
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Monthly Retainer</p>
                  <p className="text-2xl font-bold">₹{stats.tier_info.retainer.toLocaleString()}</p>
                </div>
              )}
              <div>
                <p className="text-sm text-muted-foreground mb-1">This Month Revenue</p>
                <p className="text-2xl font-bold">₹{stats.monthly_revenue?.toFixed(2) || '0.00'}</p>
              </div>
            </div>
            
            {stats.tier_info.equity_eligible && (
              <div className="mt-4 p-3 bg-green-100 border border-green-200 rounded-lg">
                <p className="text-sm font-semibold text-green-700">
                  🎉 Equity Eligible: 10% of 24-month revenue as equity-linked instrument
                </p>
              </div>
            )}
            
            {stats.next_tier && (
              <div className="mt-4 p-4 bg-white/50 rounded-lg border border-border">
                <p className="text-sm font-medium mb-2">Next Tier: {stats.next_tier.tier_name}</p>
                {stats.next_tier.revenue_needed > 0 && (
                  <p className="text-sm text-muted-foreground">
                    • Generate ₹{stats.next_tier.revenue_needed.toLocaleString()} more in monthly revenue
                  </p>
                )}
                {stats.next_tier.tenure_needed > 0 && (
                  <p className="text-sm text-muted-foreground">
                    • {stats.next_tier.tenure_needed} more months as partner
                  </p>
                )}
              </div>
            )}
            
            <div className="mt-4 text-xs text-muted-foreground">
              Partner for {stats.tenure_months} months
            </div>
          </CardContent>
        </Card>
      )}

      {/* Recent Referrals */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Referrals</CardTitle>
        </CardHeader>
        <CardContent>
          {stats?.recent_referrals?.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No referrals yet. Start adding referrals to track your commissions!</p>
          ) : (
            <div className="space-y-4">
              {stats?.recent_referrals?.map((referral) => (
                <div
                  key={referral.referral_id}
                  data-testid={`referral-${referral.referral_id}`}
                  className="flex items-center justify-between p-4 border border-border rounded-lg hover:bg-accent/50 transition-colors"
                >
                  <div>
                    <p className="font-medium">{referral.customer_name}</p>
                    <p className="text-sm text-muted-foreground">
                      {referral.customer_email || referral.customer_phone || 'No contact'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium">₹{(referral.total_commission_earned || referral.commission_amount || 0).toFixed(2)}</p>
                    <p className={`text-sm capitalize ${
                      referral.status === 'paid' ? 'text-green-500' : 'text-orange-500'
                    }`}>
                      {referral.status}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
